import { useDraggable } from '@dnd-kit/core';
import { useCallback, useMemo, useState } from 'react';
import { eachDayOfInterval, endOfWeek, format, isToday, isWeekend as fnsIsWeekend, parseISO, startOfWeek } from 'date-fns';
import type { CustomField, Task } from '@/lib/schemas';
import type { SprintDef } from '@/hooks/use-calendar';
import { useI18n } from '@/lib/i18n';
import { CalendarDayCell } from './calendar-day-cell';
import { CalendarEventCard } from './calendar-event-card';
import { CalendarContextMenu, type CalendarContextMenuItem } from './calendar-context-menu';
import { colorToHex, resolveCalendarColor, resolveTaskDate, extractIsoDate, type CalendarDateSourceWithAll } from './calendar-config';

// ============================================
// DRAGGABLE EVENT (Week)
// ============================================
//
// Mirror of the Month-view draggable so the pill in Week stays
// grabbable. Kept as a separate component (vs. shared) so each grid
// can tune the wrapper class without dragging the other along.

interface DraggableEventProps {
    task: Task;
    colorHex: string;
    isStart: boolean;
    isEnd: boolean;
    isSingleDay: boolean;
    onClick?: (task: Task) => void;
}

function DraggableEvent({
    task,
    colorHex,
    isStart,
    isEnd,
    isSingleDay,
    onClick,
}: DraggableEventProps) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `calendar-event-${task.id}`,
        data: { type: 'calendar-event', taskId: task.id },
    });

    return (
        <div
            ref={setNodeRef}
            style={isDragging ? { opacity: 0 } : undefined}
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none"
        >
            <CalendarEventCard
                task={task}
                colorHex={colorHex}
                isStart={isStart}
                isEnd={isEnd}
                isSingleDay={isSingleDay}
                onClick={onClick}
            />
        </div>
    );
}

// ============================================
// WEEK VIEW
// ============================================
//
// t16: a 7-column grid for the currently-visible week (Mon → Sun).
// Same shape as Month but denser — taller cells, no out-of-month
// dimming (every day is "current"). Used as the alternative to Month
// in the view-mode selector.

export interface CalendarWeekProps {
    anchor: Date;
    tasks: Task[];
    customFields: CustomField[];
    sprints: SprintDef[];
    sprintField: CustomField | null;
    /** T21 R4: user-defined date ranges that render as bands. */
    periods?: import('./calendar-config').PeriodDef[];
    colorBy: string;
    dateSource: CalendarDateSourceWithAll;
    onTaskClick?: (task: Task) => void;
    /** t18: right-click on a day cell — parent opens the create-task dialog pre-filled with this day. */
    onCreateTaskAtDate?: (dateIso: string) => void;
    /** t18: right-click on a task pill — parent opens the delete confirmation. */
    onDeleteTask?: (taskId: string) => void;
    canEdit?: boolean;
}

export function CalendarWeek({
    anchor,
    tasks,
    customFields,
    sprints,
    sprintField,
    periods,
    colorBy,
    dateSource,
    onTaskClick,
    onCreateTaskAtDate,
    onDeleteTask,
    canEdit,
}: CalendarWeekProps) {
    const { t } = useI18n();

    const gridStart = startOfWeek(anchor, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(anchor, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

    // t18: same day-1 fix as CalendarMonth — derive day-key via
    // extractIsoDate so UTC midnight tasks never shift back in non-UTC
    // timezones.
    const eventsByDay = useMemo(() => {
        const map = new Map<string, Array<{
            task: Task;
            isStart: boolean;
            isEnd: boolean;
            isSingleDay: boolean;
        }>>();
        for (const task of tasks) {
            const resolution = resolveTaskDate(task, dateSource, sprints);
            if (!resolution.start) continue;
            const startKey = extractIsoDate(resolution.start);
            const endKey = extractIsoDate(resolution.end ?? resolution.start);
            if (!startKey || !endKey) continue;
            const single = startKey === endKey;
            const startDay = parseISO(startKey);
            const endDay = parseISO(endKey);
            const clampedStart = startDay < gridStart ? gridStart : startDay;
            const clampedEnd = endDay > gridEnd ? gridEnd : endDay;
            const span = eachDayOfInterval({ start: clampedStart, end: clampedEnd });
            for (const day of span) {
                const key = format(day, 'yyyy-MM-dd');
                const list = map.get(key) ?? [];
                list.push({
                    task,
                    isStart: key === startKey,
                    isEnd: key === endKey,
                    isSingleDay: single,
                });
                map.set(key, list);
            }
        }
        return map;
    }, [tasks, sprints, dateSource, gridStart, gridEnd]);

    // t18: right-click context menu (same shape as CalendarMonth).
    const [menuState, setMenuState] = useState<{
        open: boolean;
        x: number;
        y: number;
        mode: 'day' | 'task';
        dateIso?: string;
        task?: Task;
    }>({ open: false, x: 0, y: 0, mode: 'day' });

    const closeMenu = useCallback(() => {
        setMenuState((s) => ({ ...s, open: false }));
    }, []);

    const openDayMenu = useCallback((dateIso: string, ev: React.MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        setMenuState({
            open: true,
            x: ev.clientX,
            y: ev.clientY,
            mode: 'day',
            dateIso,
        });
    }, []);

    const openTaskMenu = useCallback((task: Task, ev: React.MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        setMenuState({
            open: true,
            x: ev.clientX,
            y: ev.clientY,
            mode: 'task',
            task,
        });
    }, []);

    const menuItems = useMemo<CalendarContextMenuItem[]>(() => {
        if (!menuState.open) return [];
        if (menuState.mode === 'day' && menuState.dateIso && onCreateTaskAtDate) {
            const date = menuState.dateIso;
            let label = t('calendar.contextMenu.createTaskAt');
            try {
                const friendly = format(parseISO(date), 'EEEE, MMM d');
                label = label.replace('{date}', friendly);
            } catch {
                label = label.replace('{date}', date);
            }
            return [
                {
                    id: 'create-task',
                    label,
                    icon: 'create',
                    onClick: () => onCreateTaskAtDate(date),
                },
            ];
        }
        if (menuState.mode === 'task' && menuState.task) {
            const task = menuState.task;
            const items: CalendarContextMenuItem[] = [];
            if (onTaskClick) {
                items.push({
                    id: 'open-task',
                    label: t('calendar.contextMenu.openTask'),
                    icon: 'open',
                    onClick: () => onTaskClick(task),
                });
            }
            if (onDeleteTask) {
                items.push({
                    id: 'delete-task',
                    label: t('calendar.contextMenu.deleteTask'),
                    icon: 'delete',
                    danger: true,
                    onClick: () => onDeleteTask(task.id),
                });
            }
            return items;
        }
        return [];
    }, [menuState, onCreateTaskAtDate, onTaskClick, onDeleteTask, t]);

    return (
        // T30 R6: Week view scrolls horizontally on phones. The
        // outer wrapper keeps the border + rounded corners; the
        // inner overflow-x-auto lets the 7-column grid stretch to a
        // minimum readable width and pan left/right on narrow
        // viewports. The wrapper therefore drops `overflow-hidden`
        // on most viewports — we keep `overflow-hidden` at the outer
        // div but only for the rounded-corner clip. sm:overflow-visible
        // would break the rounded corners.
        <div className="rounded-lg border border-white/10 bg-surface/30 overflow-hidden">
            <div className="overflow-x-auto">
            <div className="grid grid-cols-7 min-w-[36rem] border-b border-white/10 bg-surface/50">
                {days.map((day) => (
                    <div
                        key={format(day, 'yyyy-MM-dd')}
                        className="px-1 sm:px-2 py-1.5 text-[10px] sm:text-[11px] font-medium uppercase tracking-wider text-foreground-muted"
                    >
                        {t(`calendar.weekday.${day.getDay()}` as any)}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 min-w-[36rem]" style={{ minHeight: '560px' }}>
                {days.map((day) => {
                    const key = format(day, 'yyyy-MM-dd');
                    const events = eventsByDay.get(key) ?? [];
                    return (
                        <CalendarDayCell
                            key={key}
                            id={`week-day-${key}`}
                            date={day}
                            isToday={isToday(day)}
                            isWeekend={fnsIsWeekend(day)}
                            sprints={sprints}
                            periods={periods}
                            compact
                            className="border-r border-white/10 last:border-r-0"
                            onContextMenu={(e) => openDayMenu(key, e)}
                        >
                            {events.map(({ task, isStart, isEnd, isSingleDay }) => {
                                const colorHex = colorToHex(
                                    resolveCalendarColor(task, colorBy, sprints, sprintField),
                                    customFields
                                );
                                const pill = canEdit !== false ? (
                                    <DraggableEvent
                                        key={`${task.id}-${key}`}
                                        task={task}
                                        colorHex={colorHex}
                                        isStart={isStart}
                                        isEnd={isEnd}
                                        isSingleDay={isSingleDay}
                                        onClick={onTaskClick}
                                    />
                                ) : (
                                    <CalendarEventCard
                                        key={`${task.id}-${key}`}
                                        task={task}
                                        colorHex={colorHex}
                                        isStart={isStart}
                                        isEnd={isEnd}
                                        isSingleDay={isSingleDay}
                                        onClick={onTaskClick}
                                    />
                                );
                                return (
                                    <div
                                        key={`${task.id}-${key}-wrap`}
                                        onContextMenu={(e) => openTaskMenu(task, e)}
                                        data-testid={`calendar-pill-wrap-${task.id}-${key}`}
                                    >
                                        {pill}
                                    </div>
                                );
                            })}
                        </CalendarDayCell>
                    );
                })}
            </div>
            </div>

            <CalendarContextMenu
                open={menuState.open}
                x={menuState.x}
                y={menuState.y}
                items={menuItems}
                onClose={closeMenu}
            />
        </div>
    );
}
